import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { RefreshIcon, MailIcon } from "../components/icons";
import { formatDate } from "../utils/dateUtils";
import {
  isPushSupported,
  getPushPermission,
  getExistingSubscription,
  enablePush,
  disablePush,
} from "../utils/pushSubscription";

// Single inbox for every notification across the app. Bureau members and
// public seekers share this page; the only difference is the API base.
const baseFor = () =>
  typeof window !== "undefined" && localStorage.getItem("publicUserToken")
    ? "/public/notifications"
    : "/notifications";

const typeIcon = (type) => {
  // Single-glyph badges keep the row dense — full icons would compete with
  // the surrounding sidebar/NavBar iconography.
  const map = {
    referral_created: "🔔",
    referral_updated: "🔁",
    interest_received: "📩",
    interest_approved: "✅",
    interest_rejected: "⛔",
  };
  return map[type] || "•";
};

const Notifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pushPerm, setPushPerm] = useState("default");
  const [pushSubbed, setPushSubbed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading notifications…");
      setGlobalLoading(true);
      const res = await apiFetch(`${baseFor()}?limit=50`);
      setItems(res?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load notifications");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync push enable state on mount.
  useEffect(() => {
    if (!isPushSupported()) return;
    setPushPerm(getPushPermission());
    getExistingSubscription()
      .then((s) => setPushSubbed(!!s))
      .catch(() => {});
  }, []);

  const markAllRead = async () => {
    try {
      await apiFetch(`${baseFor()}/mark-all-read`, { method: "POST" });
      setItems((prev) =>
        prev.map((it) => ({ ...it, read: true, readAt: it.readAt || new Date().toISOString() }))
      );
      // Drop the bell badge to 0 instantly. NavBar listens for this.
      window.dispatchEvent(new CustomEvent("notifications:cleared"));
    } catch (err) {
      setError(err.message || "Failed to mark all read");
    }
  };

  const openItem = async (it) => {
    if (!it.read) {
      apiFetch(`${baseFor()}/${it._id}/read`, { method: "POST" }).catch(() => {});
      setItems((prev) =>
        prev.map((x) =>
          x._id === it._id ? { ...x, read: true, readAt: new Date().toISOString() } : x
        )
      );
      // Decrement the bell badge instantly. Only fires when we actually
      // transitioned this row from unread → read.
      window.dispatchEvent(new CustomEvent("notifications:read-one"));
    }
    if (it.url) navigate(it.url);
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushSubbed) {
        await disablePush();
        setPushSubbed(false);
      } else {
        const sub = await enablePush();
        setPushSubbed(!!sub);
        setPushPerm(getPushPermission());
      }
    } catch (err) {
      setError(err.message || "Push toggle failed");
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <MailIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Notifications
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Referrals, interest decisions, and other updates
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button
              onClick={markAllRead}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-3 py-1.5 text-sm font-medium transition-colors"
            >
              Mark all read
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Push enable card — only renders if the browser supports it. */}
        {isPushSupported() && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--color-text-strong)]">
                Browser push notifications
              </div>
              <div className="text-xs text-[var(--color-text-muted)]">
                {pushPerm === "denied"
                  ? "Blocked in your browser settings. Allow notifications for this site to enable."
                  : pushSubbed
                  ? "Enabled on this device. You'll be notified even when this tab isn't open."
                  : "Turn on to be notified instantly when something happens."}
              </div>
            </div>
            <button
              onClick={togglePush}
              disabled={pushBusy || pushPerm === "denied"}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
                pushSubbed
                  ? "border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
                  : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)]"
              }`}
            >
              {pushBusy
                ? "Working…"
                : pushSubbed
                ? "Turn off"
                : "Turn on"}
            </button>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!loading && items.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            You're all caught up.
          </div>
        )}

        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it._id}
              onClick={() => openItem(it)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openItem(it);
                }
              }}
              className={`bg-[var(--color-surface)] border rounded-lg p-3 cursor-pointer transition-colors flex items-start gap-3 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${
                it.read
                  ? "border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]"
                  : "border-[var(--color-primary)] bg-[var(--color-primary-soft)]"
              }`}
            >
              <span className="text-lg shrink-0 leading-none mt-0.5">
                {typeIcon(it.type)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm font-semibold text-[var(--color-text-strong)] truncate">
                    {it.title}
                  </div>
                  <div className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
                    {formatDate(it.createdAt)}
                  </div>
                </div>
                {it.body && (
                  <div className="text-xs text-[var(--color-text)] mt-0.5 whitespace-pre-wrap">
                    {it.body}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Notifications;
